import {useEffect, useState, ReactNode} from 'react'
import {Link, useNavigate, useParams, useSearchParams, Navigate} from 'react-router-dom'
import {BRAND, CONTACT, DEPOSIT} from './site'
import {Container, AreaMap} from './ui'
import {useLightbox} from './Lightbox'
import {api, ApiError, Listing, RoomBed, listingImageSrc} from '../../services/api'
import {useAuth} from '../../modules/auth'
import {Role} from '../../types/domain'
import {useToast} from '../../components/Toast'
import {Spinner} from '../../components/Spinner'

const icon = (path: ReactNode) => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    {path}
  </svg>
)
const I_BED = icon(<><path d='M2 17v-5a2 2 0 0 1 2-2h13a3 3 0 0 1 3 3v4' /><path d='M2 17h20' /><path d='M2 11V7' /></>)
const I_BATH = icon(<><path d='M4 12h16a1 1 0 0 1 1 1v2a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4v-2a1 1 0 0 1 1-1z' /><path d='M6 12V5a2 2 0 0 1 4 0' /></>)
const I_PIN = icon(<><path d='M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z' /><circle cx='12' cy='10' r='3' /></>)

const ListingDetailPage = () => {
  const {slug} = useParams()
  const {open} = useLightbox()
  const [active, setActive] = useState(0)
  const [listing, setListing] = useState<Listing | null>(null)
  const [similar, setSimilar] = useState<Listing[]>([])
  const [status, setStatus] = useState<'loading' | 'ready' | 'notfound'>('loading')

  useEffect(() => {
    if (!slug) return
    setStatus('loading')
    setActive(0)
    Promise.all([api.publicListing(slug), api.publicListings().catch(() => [])])
      .then(([one, all]) => {
        setListing(one)
        setSimilar(all.filter((a) => a.slug !== one.slug))
        setStatus('ready')
      })
      .catch((err) => setStatus(err instanceof ApiError && err.status === 404 ? 'notfound' : 'notfound'))
  }, [slug])

  if (status === 'notfound') {
    return <Navigate to='/rooms' replace />
  }
  if (status === 'loading' || !listing) {
    return (
      <div style={{background: BRAND.soft, minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: BRAND.muted}}>
        Loading…
      </div>
    )
  }
  // Headline "from" price = the lowest bed rate in this room (server-computed),
  // falling back to the room's static price text if it has no beds.
  const fromPrice = listing.fromPrice ? `$${Number(listing.fromPrice).toFixed(0)}` : listing.price
  // Public pages show the general location only — the exact street address is
  // private to approved renters and revealed in the portal after approval.
  const addr = CONTACT.publicLocation

  const specs = [
    {icon: I_BED, label: 'Beds', value: String(listing.bedsCount)},
    {icon: I_BATH, label: 'Bathroom', value: listing.baths},
  ]

  return (
    <div style={{background: BRAND.soft, paddingBottom: 64}}>
      {/* Title bar */}
      <div style={{background: '#fff', borderBottom: '1px solid #eef0f4'}}>
        <Container style={{padding: '26px 20px'}}>
          <Link to='/rooms' style={{color: BRAND.green, textDecoration: 'none', fontWeight: 600, fontSize: 14}}>← All rooms</Link>
          <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginTop: 12}}>
            <div>
              <h1 style={{fontSize: 30, fontWeight: 800, color: BRAND.ink, margin: '0 0 6px'}}>{listing.title}</h1>
              <div style={{display: 'flex', alignItems: 'center', gap: 7, color: BRAND.muted, fontSize: 15}}>
                <span style={{color: '#aab0bf', display: 'inline-flex'}}>{I_PIN}</span>
                {addr}
              </div>
            </div>
            <div style={{textAlign: 'right'}}>
              <div style={{color: BRAND.green, fontSize: 30, fontWeight: 800, lineHeight: 1}}>{fromPrice}</div>
              <div style={{color: BRAND.muted, fontSize: 14}}>per {listing.priceUnit}</div>
            </div>
          </div>
        </Container>
      </div>

      <Container style={{paddingTop: 28}}>
        {/* Gallery */}
        <div style={{borderRadius: 18, overflow: 'hidden', boxShadow: '0 10px 30px rgba(27,27,41,0.08)', background: '#fff'}}>
          <img src={listingImageSrc(listing.gallery[active])} alt={listing.title} onClick={() => open(listing.gallery.map((src) => ({src: listingImageSrc(src), title: listing.title})), active)} style={{width: '100%', height: 'clamp(260px, 46vw, 520px)', objectFit: 'cover', display: 'block', cursor: 'zoom-in'}} />
          <div style={{display: 'flex', gap: 8, padding: 10, overflowX: 'auto'}}>
            {listing.gallery.map((g, i) => (
              <button
                key={g}
                onClick={() => setActive(i)}
                style={{
                  flex: '0 0 auto',
                  width: 92,
                  height: 64,
                  borderRadius: 10,
                  overflow: 'hidden',
                  border: i === active ? `2px solid ${BRAND.green}` : '2px solid transparent',
                  padding: 0,
                  cursor: 'pointer',
                  background: 'none',
                }}
              >
                <img src={listingImageSrc(g)} alt='' style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}} />
              </button>
            ))}
          </div>
        </div>

        <div className='row g-4' style={{marginTop: 28}}>
          {/* Main column */}
          <div className='col-12 col-lg-8'>
            {/* Specs */}
            <div style={{background: '#fff', borderRadius: 16, border: '1px solid #eef0f4', padding: '8px 8px', display: 'flex', flexWrap: 'wrap'}}>
              {specs.map((s) => (
                <div key={s.label} style={{flex: '1 0 33%', minWidth: 120, display: 'flex', alignItems: 'center', gap: 10, padding: '16px 18px'}}>
                  <span style={{color: BRAND.green, display: 'inline-flex'}}>{s.icon}</span>
                  <div>
                    <div style={{fontSize: 12, color: BRAND.muted, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600}}>{s.label}</div>
                    <div style={{fontSize: 16, fontWeight: 700, color: BRAND.ink}}>{s.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Description */}
            <Section title='Description'>
              <p style={{color: '#475569', fontSize: 16, lineHeight: 1.75, margin: 0}}>{listing.description}</p>
            </Section>

            {/* Amenities */}
            <Section title='Amenities'>
              <div className='row g-2'>
                {listing.detailAmenities.map((a) => (
                  <div className='col-12 col-sm-6' key={a}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 10, color: BRAND.ink, fontSize: 15, padding: '6px 0'}}>
                      <span style={{flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: 'rgba(3,149,64,0.12)', color: BRAND.green, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800}}>✓</span>
                      {a}
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* Guidelines */}
            <Section title='Terms & guidelines'>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 12}}>
                {listing.rules.map((r) => (
                  <span key={r.text} style={{display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: r.allowed ? BRAND.green : '#d9214e', background: r.allowed ? 'rgba(3,149,64,0.1)' : 'rgba(217,33,78,0.08)', borderRadius: 999, padding: '8px 14px'}}>
                    {r.allowed ? '✓' : '✕'} {r.text}
                  </span>
                ))}
              </div>
              <p style={{color: BRAND.muted, fontSize: 14, margin: '16px 0 0'}}>
                {CONTACT.reserveNote}
              </p>
            </Section>

            {/* Location — general area only; the exact address is revealed to
                approved renters in the portal after deposit and approval. */}
            <Section title='Location'>
              <div style={{display: 'flex', alignItems: 'center', gap: 7, color: BRAND.muted, fontSize: 14, marginBottom: 14}}>
                <span style={{color: BRAND.green, display: 'inline-flex'}}>{I_PIN}</span>
                {addr}
              </div>
              <AreaMap query={CONTACT.mapsQuery} height={320} />
            </Section>
          </div>

          {/* Booking sidebar */}
          <div className='col-12 col-lg-4'>
            <div style={{position: 'sticky', top: 88, background: '#fff', borderRadius: 18, border: '1px solid #eef0f4', boxShadow: '0 10px 30px rgba(27,27,41,0.06)', padding: '26px 24px'}}>
              <BedBookingPanel slug={listing.slug} />
            </div>
          </div>
        </div>

        {/* Similar listings */}
        <div style={{marginTop: 48}}>
          <h2 style={{fontSize: 22, fontWeight: 800, color: BRAND.ink, margin: '0 0 20px'}}>Similar listings</h2>
          <div className='row g-4'>
            {similar.map((a) => (
              <div className='col-12 col-md-6' key={a.slug}>
                <Link to={`/rooms/${a.slug}`} style={{textDecoration: 'none', display: 'block'}}>
                  <div className='ccp-lift' style={{background: '#fff', borderRadius: 16, overflow: 'hidden', border: '1px solid #eef0f4', boxShadow: '0 8px 22px rgba(27,27,41,0.05)'}}>
                    <div className='ccp-zoom' style={{position: 'relative', overflow: 'hidden'}}>
                      <img src={listingImageSrc(a.gallery[0])} alt={a.title} loading='lazy' style={{width: '100%', height: 200, objectFit: 'cover', display: 'block'}} />
                      <div style={{position: 'absolute', inset: 0, background: 'linear-gradient(transparent 60%, rgba(0,0,0,0.5))'}} />
                      <div style={{position: 'absolute', left: 18, bottom: 14, color: '#fff', display: 'flex', alignItems: 'baseline', gap: 4}}>
                        <span style={{fontSize: 26, fontWeight: 800}}>{a.fromPrice ? `From $${Number(a.fromPrice).toFixed(0)}` : a.price}</span>
                        <span style={{fontSize: 13, fontWeight: 600, opacity: 0.95}}>/month</span>
                      </div>
                    </div>
                    <div style={{padding: '16px 20px 20px'}}>
                      <h3 style={{fontSize: 18, fontWeight: 700, color: BRAND.ink, margin: '0 0 4px'}}>{a.title}</h3>
                      <div style={{color: BRAND.muted, fontSize: 13}}>{CONTACT.publicLocation}</div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </div>
  )
}

const ACTIVE_BOOKING = ['PENDING', 'APPROVED', 'WAITLISTED']
const TYPE_LABEL: Record<string, string> = {TWIN: 'Twin', FULL: 'Full', QUEEN: 'Queen'}

/**
 * Booking widget for a room: shows its beds with live availability. Guests are
 * prompted to sign in; renters must be verified and free of an active booking
 * before they can pick an available bed and request it.
 */
const BedBookingPanel = ({slug}: {slug: string}) => {
  const {currentUser} = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Bed code passed from the Accommodations grid (e.g. ?bed=1T) to pre-select.
  const presetBedCode = searchParams.get('bed')
  // Arriving via "Join Waitlist" on a taken bed (?waitlist=1) flips the panel
  // into waitlist mode: the unavailable bed is selectable and the request is
  // filed as WAITLISTED instead of a normal reservation.
  const waitlistMode = searchParams.get('waitlist') === '1'

  const isRenter = currentUser?.appRole === Role.RENTER
  const [beds, setBeds] = useState<RoomBed[] | null>(null)
  const [approved, setApproved] = useState(false)
  const [hasActive, setHasActive] = useState(false)
  const [ready, setReady] = useState(false)

  const [selected, setSelected] = useState<string | null>(null)
  const [startDate, setStartDate] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const bedList = await api.roomBeds(slug)
        if (!alive) return
        setBeds(bedList)
        // Pre-select the bed chosen on the Accommodations page. In waitlist mode
        // we select the (taken) bed regardless; otherwise only if still open.
        if (presetBedCode) {
          const match = bedList.find((b) => b.code === presetBedCode && (waitlistMode || b.available))
          if (match) setSelected(match.id)
        }
        if (isRenter) {
          const [app, mine] = await Promise.all([
            api.getMyApplication().catch(() => null),
            api.myBookings().catch(() => []),
          ])
          if (!alive) return
          setApproved(app?.status === 'APPROVED')
          setHasActive(mine.some((b) => ACTIVE_BOOKING.includes(b.status)))
        }
      } finally {
        if (alive) setReady(true)
      }
    })()
    return () => {
      alive = false
    }
  }, [slug, isRenter, presetBedCode, waitlistMode])

  const submit = async () => {
    if (!selected || !startDate) return
    setSubmitting(true)
    try {
      if (waitlistMode) {
        await api.joinWaitlist({bedId: selected, startDate})
        toast.success('You’re on the waitlist — we’ll notify you when it frees up.')
      } else {
        await api.createBooking({bedId: selected, startDate})
        toast.success('Bed requested — we’ll confirm it soon.')
      }
      navigate('/home')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : waitlistMode ? 'Could not join the waitlist' : 'Could not request this bed')
      setSubmitting(false)
    }
  }

  if (!ready || beds === null) {
    return <div style={{textAlign: 'center', color: BRAND.muted, fontSize: 14, padding: '10px 0'}}>Loading beds…</div>
  }

  // Renter who can actually book
  const canBook = isRenter && approved && !hasActive

  // Monthly-subscription model: no fixed term or upfront total — just the
  // chosen bed's monthly rate, billed monthly from move-in until cancelled.
  const selectedBed = beds.find((b) => b.id === selected) ?? null
  const monthlyRate = selectedBed ? Number(selectedBed.pricePerMonth).toFixed(2) : null
  const canSubmit = !!selected && !!startDate && !submitting

  const bedTiles = (
    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16}}>
      {beds!.map((b) => {
        // Normal mode: pick an available bed. Waitlist mode: pick a taken bed.
        const pickable = canBook && (waitlistMode ? !b.available : b.available)
        const isSel = selected === b.id
        return (
          <button
            key={b.id}
            type='button'
            disabled={!pickable}
            onClick={() => pickable && setSelected(b.id)}
            style={{
              textAlign: 'left',
              padding: '10px 12px',
              borderRadius: 10,
              border: `1.5px solid ${isSel ? BRAND.green : '#e5e7eb'}`,
              background: isSel ? 'rgba(3,149,64,0.08)' : !b.available ? '#f6f7f9' : '#fff',
              cursor: pickable ? 'pointer' : 'not-allowed',
              opacity: !pickable && !b.available ? 0.7 : 1,
            }}
          >
            <div style={{fontWeight: 700, fontSize: 14, color: BRAND.ink}}>{b.code} · {TYPE_LABEL[b.type] ?? b.type}</div>
            {b.level && <div style={{fontSize: 12, color: BRAND.muted, marginTop: 2}}>{b.level}</div>}
            <div style={{fontSize: 13, fontWeight: 700, color: BRAND.green, marginTop: 4}}>${Number(b.pricePerMonth).toFixed(0)}<span style={{fontSize: 11, fontWeight: 600, color: BRAND.muted}}>/month</span></div>
            <span style={{display: 'inline-block', marginTop: 6, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: b.available ? '#1f9d5b' : '#d9214e', background: b.available ? 'rgba(3,149,64,0.12)' : 'rgba(217,33,78,0.1)', borderRadius: 999, padding: '2px 8px'}}>
              {b.available ? 'Available' : 'Booked'}
            </span>
          </button>
        )
      })}
    </div>
  )

  // Monthly price range across this room's beds.
  const prices = beds.map((b) => Number(b.pricePerMonth)).filter((n) => n > 0)
  const minP = prices.length ? Math.min(...prices) : 0
  const maxP = prices.length ? Math.max(...prices) : 0

  return (
    <div>
      {waitlistMode && (
        <div style={{display: 'flex', gap: 10, alignItems: 'flex-start', background: 'rgba(124,77,255,0.08)', border: '1px solid rgba(124,77,255,0.25)', borderRadius: 12, padding: '12px 14px', marginBottom: 18}}>
          <span style={{fontSize: 18, lineHeight: 1.3}}>📋</span>
          <div style={{fontSize: 13, color: BRAND.ink, lineHeight: 1.5}}>
            <strong>Joining the waitlist.</strong> This bed is taken right now. Pick your dates and we’ll hold your place — you’ll be notified the moment it frees up.
          </div>
        </div>
      )}

      {prices.length > 0 && (
        <div style={{display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 18}}>
          <span style={{fontSize: 13, color: BRAND.muted, fontWeight: 600}}>Beds from</span>
          <span style={{fontSize: 28, fontWeight: 800, color: BRAND.ink}}>${minP}{maxP > minP ? `–$${maxP}` : ''}</span>
          <span style={{fontSize: 15, color: BRAND.muted, fontWeight: 600}}>/ month</span>
        </div>
      )}

      <div style={{fontSize: 13, fontWeight: 700, color: BRAND.ink, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10}}>
        {waitlistMode ? 'Waitlist a bed' : 'Choose a bed'}
      </div>

      {beds.length === 0 ? (
        <p style={{color: BRAND.muted, fontSize: 14, margin: '0 0 14px'}}>No beds in this room yet.</p>
      ) : (
        bedTiles
      )}

      {/* Guest */}
      {!currentUser && (
        <>
          <Link to='/auth/login' className='ccp-cta' style={{display: 'block', textAlign: 'center', textDecoration: 'none', fontWeight: 700, fontSize: 16, color: '#fff', background: BRAND.green, borderRadius: 12, padding: '14px'}}>
            Sign in to book
          </Link>
          <p style={{textAlign: 'center', color: BRAND.muted, fontSize: 13, lineHeight: 1.5, margin: '12px 0 0'}}>
            Login and access information are not available immediately.
          </p>
        </>
      )}

      {/* Renter, not verified */}
      {isRenter && !approved && (
        <Link to='/apply' style={{display: 'block', textAlign: 'center', textDecoration: 'none', fontWeight: 700, fontSize: 15, color: '#fff', background: BRAND.green, borderRadius: 12, padding: '13px'}}>
          Complete your application to book
        </Link>
      )}

      {/* Renter with an active booking */}
      {isRenter && approved && hasActive && (
        <div style={{textAlign: 'center'}}>
          <p style={{color: BRAND.muted, fontSize: 14, margin: '0 0 10px'}}>You already have an active booking.</p>
          <Link to='/home' style={{color: BRAND.green, fontWeight: 700, fontSize: 14, textDecoration: 'none'}}>View it →</Link>
        </div>
      )}

      {/* Eligible renter */}
      {canBook && beds.length > 0 && (
        <>
          <label style={{display: 'block', fontSize: 13, fontWeight: 600, color: BRAND.ink, marginBottom: 6}}>Move-in date</label>
          <input
            type='date'
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{width: '100%', padding: '11px 12px', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 14, marginBottom: 12, boxSizing: 'border-box'}}
          />

          {/* Monthly-rate summary — billed monthly until the renter cancels. */}
          {selectedBed && monthlyRate && (
            <div style={{background: '#f0faf4', border: '1px solid #bbe6cd', borderRadius: 12, padding: '14px 16px', marginBottom: 14}}>
              <div style={{fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: BRAND.muted, marginBottom: 8}}>Summary</div>
              <Line label={`Bed ${selectedBed.code}`} value={startDate ? `Move-in ${startDate}` : 'Monthly'} />
              <div style={{borderTop: '1px dashed #bbe6cd', margin: '8px 0'}} />
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                <span style={{fontWeight: 700, color: BRAND.ink}}>Monthly rent</span>
                <span style={{fontWeight: 800, fontSize: 20, color: BRAND.green}}>${monthlyRate}<span style={{fontSize: 13, fontWeight: 600, color: BRAND.muted}}>/mo</span></span>
              </div>
              <p style={{fontSize: 12, color: BRAND.muted, margin: '8px 0 0'}}>
                Billed monthly until you cancel. {waitlistMode ? 'Starts once the bed frees up and an admin approves.' : 'Your card is charged monthly after an admin approves.'}
              </p>
            </div>
          )}

          <button
            type='button'
            onClick={submit}
            disabled={!canSubmit}
            className='ccp-cta'
            style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, width: '100%', textAlign: 'center', fontWeight: 700, fontSize: 16, color: '#fff', background: !canSubmit ? '#7ec79a' : BRAND.green, border: 'none', borderRadius: 12, padding: '14px', cursor: !canSubmit ? 'not-allowed' : 'pointer'}}
          >
            {submitting ? <Spinner size={15} /> : !selected ? (waitlistMode ? 'Select a bed to waitlist' : 'Select a bed') : !startDate ? 'Pick your move-in date' : waitlistMode ? 'Join the waitlist' : `Request this bed · $${monthlyRate}/mo`}
          </button>
        </>
      )}

      {currentUser && !isRenter && (
        <p style={{textAlign: 'center', color: BRAND.muted, fontSize: 13, margin: '6px 0 0'}}>Booking is for renter accounts.</p>
      )}

      {/* Mandatory 24-hour post-deposit approval warning — full verbatim text
          for guests, short form once signed in (the guest CTA already carries
          the "not available immediately" line). */}
      <div style={{marginTop: 14, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '11px 13px', fontSize: 12, lineHeight: 1.5, color: '#9a3412'}}>
        ⚠️ {currentUser ? DEPOSIT.short : DEPOSIT.warning}
      </div>
      <p style={{textAlign: 'center', color: BRAND.muted, fontSize: 13, margin: '12px 0 0'}}>Verified crew only · current active crew ID required</p>
    </div>
  )
}

const Line: React.FC<{label: string; value: string}> = ({label, value}) => (
  <div style={{display: 'flex', justifyContent: 'space-between', fontSize: 13, color: BRAND.ink, padding: '2px 0'}}>
    <span style={{color: BRAND.muted}}>{label}</span>
    <span style={{fontWeight: 600}}>{value}</span>
  </div>
)

const Section: React.FC<{title: string; children: ReactNode}> = ({title, children}) => (
  <div style={{background: '#fff', borderRadius: 16, border: '1px solid #eef0f4', padding: '24px 26px', marginTop: 20}}>
    <h2 style={{fontSize: 19, fontWeight: 800, color: BRAND.ink, margin: '0 0 16px'}}>{title}</h2>
    {children}
  </div>
)

export {ListingDetailPage}
